import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'

import { whenLabel } from '../../pages/transport/bookingWords'
import { SERVICE_NAME } from '../../pages/transport/services'
import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  DESK_PAGE_SIZE,
  DESK_QUEUE_KEY,
  DESK_STATUSES,
  DESK_VIEWS,
  deskStatusLabel,
  deskViewFrom,
  fetchDeskQueue,
  isRefundManager,
  type DeskQueueFilters,
  type DeskQueueItem,
  type DeskView,
} from '../api/transportBookings'
import { EmptyState } from '../components/EmptyState'
import { FilterTabs } from '../components/FilterTabs'
import { StatusPill } from '../components/StatusPill'
import { TransportBookingDrawer } from './TransportBookingDrawer'
import { TransportRefundsTab } from './TransportRefundsTab'

/**
 * The transport desk (M40-T12, spec §F.2, ADR-0066): the queue of paid
 * bookings the desk works from every day, one booking in a drawer, and, for
 * those who may mark one issued, the refunds due.
 *
 * The page never decides what may happen to a booking: the drawer offers what
 * the booking's `allowed_actions` says (`services/transport/journey.py`), and
 * the server refuses anything else.
 *
 * `?view=` in the URL wins over the default filter, so the dashboard banner's
 * link (T07) opens the list its count describes.
 */

const SECTIONS = [
  { key: 'bookings', label: 'Bookings' },
  { key: 'refunds', label: 'Refunds due' },
] as const
type Section = (typeof SECTIONS)[number]['key']

const INPUT =
  'h-9 rounded-[8px] border border-line-2 bg-bg-app px-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
const BUTTON =
  'inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3.5 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-50'

/** How often the queue refreshes while open: drivers phone in all day. */
const REFETCH_MS = 60_000

const EMPTY_WORDS: Record<DeskView, string> = {
  next_48h_unassigned: 'No booking needs a driver in the next 48 hours.',
  attention: 'No booking needs attention.',
  past_cutoff: 'No booking is past its cut-off without a driver.',
  all: 'No paid bookings yet.',
}

export function TransportBookingsPage(): ReactNode {
  const { user } = useAuth()
  const refunds = isRefundManager(user?.role)
  const [section, setSection] = useState<Section>('bookings')

  return (
    <section
      className="flex flex-col gap-5"
      aria-labelledby="transport-desk-heading"
    >
      <div>
        <h1
          id="transport-desk-heading"
          className="font-display text-2xl font-semibold text-ink-1"
        >
          Transport bookings
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Paid Medcor Transport bookings: assign a driver, inform the hospital,
          and follow each trip through the day. Scheduled, non-emergency
          transport only.
        </p>
      </div>

      {refunds ? (
        <FilterTabs
          tabs={SECTIONS}
          active={section}
          onChange={setSection}
          label="Transport desk"
        />
      ) : null}

      {refunds && section === 'refunds' ? <TransportRefundsTab /> : <Queue />}
    </section>
  )
}

function Queue(): ReactNode {
  const { token } = useAuth()
  const [search, setSearch] = useSearchParams()
  const view = deskViewFrom(search)
  const [status, setStatus] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)

  // The page belongs to one set of filters: any change, including a new
  // `?view=` from a link, starts again at the first page.
  const filterKey = [view, status, dateFrom, dateTo].join('|')
  const [page, setPage] = useState({ key: filterKey, offset: 0 })
  const offset = page.key === filterKey ? page.offset : 0

  const filters: DeskQueueFilters = {
    view,
    status: status || null,
    dateFrom,
    dateTo,
    offset,
  }
  // Said here in words, rather than sent for the server to refuse.
  const backwards = dateFrom !== '' && dateTo !== '' && dateFrom > dateTo
  const queue = useQuery({
    queryKey: [...DESK_QUEUE_KEY, filters],
    queryFn: () => fetchDeskQueue(token, filters),
    enabled: Boolean(token) && !backwards,
    refetchInterval: REFETCH_MS,
    placeholderData: keepPreviousData,
  })

  // Stable, so a refetch does not re-run the drawer's focus effect.
  const closeDrawer = useCallback(() => setOpenId(null), [])

  const items = queue.data?.data ?? []
  const total = queue.data?.total ?? 0
  const filtered = Boolean(status || dateFrom || dateTo)

  return (
    <>
      <div className="flex flex-col gap-3">
        <FilterTabs
          tabs={DESK_VIEWS}
          active={view}
          onChange={(next) => setSearch({ view: next })}
          label="Which bookings to show"
        />
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Status
            <select
              className={INPUT}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              {DESK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {deskStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Pickup from
            <input
              type="date"
              className={INPUT}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-ink-2">
            Pickup to
            <input
              type="date"
              className={INPUT}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
        </div>
      </div>

      {queue.isError && queue.data ? (
        <p
          role="alert"
          className="rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-2 text-sm font-medium text-bed-bad"
        >
          Could not refresh the list. It may be out of date.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void queue.refetch()}
          >
            Retry
          </button>
        </p>
      ) : null}

      {backwards ? (
        <p role="alert" className="text-sm font-medium text-bed-bad">
          “Pickup from” is after “Pickup to”. Change one of the dates.
        </p>
      ) : queue.isPending ? (
        <div
          role="status"
          aria-label="Loading transport bookings"
          className="h-40 w-full animate-pulse rounded-2xl bg-bg-sunken"
        />
      ) : queue.isError && !queue.data ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
        >
          <p className="text-sm font-medium text-bed-bad">
            {queue.error instanceof ApiError
              ? queue.error.message
              : 'Could not load the bookings.'}
          </p>
          <button
            type="button"
            className={BUTTON}
            onClick={() => void queue.refetch()}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          <EmptyState title="No bookings here.">
            {offset > 0
              ? // A later page emptied under the desk: its bookings were
                // assigned, or moved out of this view, since it was opened.
                'This page is empty now.'
              : filtered
                ? 'No booking matches these filters.'
                : EMPTY_WORDS[view]}
          </EmptyState>
          {offset > 0 ? (
            <div className="flex justify-center pb-6">
              <button
                type="button"
                className={BUTTON}
                onClick={() => setPage({ key: filterKey, offset: 0 })}
              >
                Back to the first page
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-2" aria-label="Transport bookings">
            {items.map((item) => (
              <li key={item.id}>
                <QueueRow item={item} onOpen={() => setOpenId(item.id)} />
              </li>
            ))}
          </ul>
          {total > DESK_PAGE_SIZE || offset > 0 ? (
            <nav
              aria-label="Pages"
              className="flex flex-wrap items-center justify-between gap-3 text-sm text-ink-2"
            >
              <span>
                {offset + 1}–{Math.min(offset + DESK_PAGE_SIZE, total)} of{' '}
                {total}
              </span>
              <span className="flex gap-2">
                <button
                  type="button"
                  className={BUTTON}
                  disabled={offset === 0}
                  onClick={() =>
                    setPage({
                      key: filterKey,
                      offset: Math.max(0, offset - DESK_PAGE_SIZE),
                    })
                  }
                >
                  Previous page
                </button>
                <button
                  type="button"
                  className={BUTTON}
                  disabled={offset + DESK_PAGE_SIZE >= total}
                  onClick={() =>
                    setPage({ key: filterKey, offset: offset + DESK_PAGE_SIZE })
                  }
                >
                  Next page
                </button>
              </span>
            </nav>
          ) : null}
        </div>
      )}

      <TransportBookingDrawer bookingId={openId} onClose={closeDrawer} />
    </>
  )
}

/** One booking in the queue. Past cut-off is red AND says so in words. */
function QueueRow({
  item,
  onOpen,
}: {
  item: DeskQueueItem
  onOpen: () => void
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-late={item.past_cutoff ? 'true' : undefined}
      className={`flex w-full flex-wrap items-center gap-x-4 gap-y-1 rounded-[12px] border px-4 py-3 text-left text-sm hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
        item.past_cutoff
          ? 'border-bed-bad bg-bed-bad-bg'
          : 'border-line-1 bg-bg-app'
      }`}
    >
      <span className="font-mono font-semibold text-ink-1">
        {item.reference}
      </span>
      {item.past_cutoff ? (
        <span className="rounded-full bg-bed-bad px-2 py-0.5 text-xs font-semibold text-white">
          Past cut-off
        </span>
      ) : null}
      {item.over_capacity ? (
        <span className="rounded-full bg-bed-warn-bg px-2 py-0.5 text-xs font-semibold text-ink-1">
          Over capacity
        </span>
      ) : null}
      {item.return_requested ? (
        <span className="rounded-full bg-bed-warn-bg px-2 py-0.5 text-xs font-semibold text-ink-1">
          Return requested
        </span>
      ) : null}
      <span className="text-ink-1">{whenLabel(item.pickup_at)}</span>
      <span className="text-ink-2">
        {SERVICE_NAME[item.service as keyof typeof SERVICE_NAME] ??
          item.service}
      </span>
      <span className="min-w-0 flex-1 truncate text-ink-2">
        {item.pickup_area} to {item.hospital}
      </span>
      <span className="text-ink-2">
        {item.booker_name} · {item.booker_phone_masked}
      </span>
      <StatusPill status={item.status} label={deskStatusLabel(item.status)} />
    </button>
  )
}
